import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";

interface ThreadDetail {
  _id: string;
  title: string;
  body: string;
  isResolved: boolean;
  createdAt: number;
  asker: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
  isMine: boolean;
  canResolve: boolean;
  replies: {
    _id: string;
    body: string;
    createdAt: number;
    isMe: boolean;
    author: {
      firstName: string | null;
      lastName: string | null;
      email: string;
    } | null;
  }[];
}

// Q&A thread detail — title + opening message at top, replies under-
// neath, reply form at the bottom. Asker or landlord can mark
// resolved/unresolved (Convex enforces).
export function QaThreadDialog({
  open,
  onClose,
  threadId,
}: {
  open: boolean;
  onClose: () => void;
  threadId: string | null;
}) {
  const thread = useQuery(
    anyApi.qa.getThread,
    open && threadId ? { threadId } : "skip",
  ) as ThreadDetail | null | undefined;
  const reply = useMutation(anyApi.qa.reply);
  const setResolved = useMutation(anyApi.qa.setResolved);

  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!threadId) return;
    setSubmitting(true);
    setError(null);
    try {
      await reply({ threadId, body });
      setBody("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleResolved = async () => {
    if (!thread) return;
    try {
      await setResolved({
        threadId: thread._id,
        isResolved: !thread.isResolved,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={thread?.title ?? "Question"}
      width="lg"
    >
      <div className="px-6 py-5 space-y-5">
        {thread === undefined ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : thread === null ? (
          <p className="text-sm text-slate-500">
            Thread not found, or you don't have access.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {thread.isResolved ? (
                <Badge tone="emerald">Resolved</Badge>
              ) : (
                <Badge tone="amber">Open</Badge>
              )}
              <span className="text-xs text-slate-500">
                Asked by {thread.isMine ? "you" : authorName(thread.asker)}{" "}
                · {timeAgo(thread.createdAt)}
              </span>
            </div>

            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {thread.body}
            </p>

            {thread.replies.length > 0 && (
              <section>
                <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
                  Replies
                </h3>
                <ul className="mt-3 space-y-2">
                  {thread.replies.map((r) => (
                    <li
                      key={r._id}
                      className={`rounded-md border px-3 py-2 ${
                        r.isMe
                          ? "border-slate-300 bg-slate-50"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-slate-700">
                          {r.isMe ? "You" : authorName(r.author)}
                        </span>
                        <span className="text-xs text-slate-400">
                          {timeAgo(r.createdAt)}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-slate-700 whitespace-pre-wrap">
                        {r.body}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <form onSubmit={handleReply} className="space-y-2">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                placeholder="Write a reply…"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 resize-none"
              />
              <div className="flex items-center justify-between gap-3">
                {thread.canResolve ? (
                  <button
                    type="button"
                    onClick={handleToggleResolved}
                    className="text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    {thread.isResolved
                      ? "Mark unresolved"
                      : "Mark resolved"}
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="submit"
                  disabled={submitting || !body.trim()}
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {submitting ? "Posting…" : "Post reply"}
                </button>
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </form>
          </>
        )}
      </div>
    </Dialog>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: "emerald" | "amber";
  children: React.ReactNode;
}) {
  const styles =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : "bg-amber-50 text-amber-700 ring-amber-200";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles}`}
    >
      {children}
    </span>
  );
}

function authorName(a: ThreadDetail["asker"]): string {
  if (!a) return "Unknown";
  if (a.firstName) {
    return a.lastName ? `${a.firstName} ${a.lastName.charAt(0)}.` : a.firstName;
  }
  return a.email.split("@")[0];
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
